/**
 * Portal Customer List Page.
 *
 * Displays a searchable, filterable, sortable list of customers within the portal layout.
 * Defaults to active customers sorted by YTD sales (highest first).
 * Clicking a customer navigates to the portal customer detail (360) page.
 *
 * Route: /portal/customers
 */

import { useCallback, useEffect, useState } from 'react';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { formatMoney } from '../../utils/formatting';
import { customerService, type CustomerListParams } from '../../services/customers';
import portalService from '../../services/portal';
import type { CustomerListItem, RegionalResponsibleOption } from '../../types/customer';
import { CUSTOMER_STATUS_LABELS } from '../../types/customer';
import AddCustomerModal from '../../components/portal/AddCustomerModal';
import QualityAsksCard from '../../components/portal/qualityAsks/QualityAsksCard';
import ComplaintPill from '../../components/portal/complaints/ComplaintPill';
import ReportComplaintModal from '../../components/portal/complaints/ReportComplaintModal';
import { summariesByCustomer } from '../../components/portal/complaints/complaintView';
import { getCustomerQualitySummary, type CustomerQualitySummary } from '../../services/portalQualityAsks';
import { trackEvent } from '../../services/portalTracking';
import styles from './PortalCustomersPage.module.css';

const LIMIT = 50;

const STATUS_COLORS: Record<string, { bg: string; fg: string }> = {
  A: { bg: '#dcfce7', fg: '#16a34a' },
  I: { bg: '#fee2e2', fg: '#dc2626' },
  T: { bg: '#fef3c7', fg: '#92400e' },
};

type SortKey =
  | 'customer_name'
  | 'city'
  | 'state'
  | 'salesperson_name'
  | 'regional_responsible_name'
  | 'ytd_sales'
  | 'lytd_sales'
  | 'ly_total_sales'
  | 'all_time_sales';

type SortDir = 'asc' | 'desc';

/** Default sort direction when a column is first clicked. */
const DEFAULT_SORT_DIR: Record<SortKey, SortDir> = {
  customer_name: 'asc',
  city: 'asc',
  state: 'asc',
  salesperson_name: 'asc',
  regional_responsible_name: 'asc',
  ytd_sales: 'desc',
  lytd_sales: 'desc',
  ly_total_sales: 'desc',
  all_time_sales: 'desc',
};

function SortIcon({ active, direction }: { active: boolean; direction: SortDir }) {
  if (!active) {
    return <span className={styles.sortIcon} aria-hidden="true">&#8597;</span>;
  }
  return (
    <span className={`${styles.sortIcon} ${styles.sortIconActive}`} aria-hidden="true">
      {direction === 'asc' ? '\u2191' : '\u2193'}
    </span>
  );
}

export default function PortalCustomersPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const hasSalesperson = Boolean(user?.salesperson_id);
  const urlSalesperson = searchParams.get('salesperson_no') ?? '';
  const [customers, setCustomers] = useState<CustomerListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [salespersonFilter, setSalespersonFilter] = useState(urlSalesperson);
  const [regionalFilter, setRegionalFilter] = useState(
    searchParams.get('regional_responsible') ?? ''
  );
  const [myCustomers, setMyCustomers] = useState(urlSalesperson ? false : hasSalesperson);
  const [showInactive, setShowInactive] = useState(false);
  const [offset, setOffset] = useState(0);
  const [sortBy, setSortBy] = useState<SortKey>('ytd_sales');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showComplaintModal, setShowComplaintModal] = useState(false);
  // Open / recently completed complaints for the customers on screen
  // (kanban 2a28d4c0); a failed fetch just shows no pills.
  const [qualityByCustomer, setQualityByCustomer] = useState<Map<string, CustomerQualitySummary>>(new Map());
  useEffect(() => {
    let cancelled = false;
    const nos = customers.map((c) => c.sage_customer_no).filter(Boolean);
    getCustomerQualitySummary(nos)
      .then((rows) => { if (!cancelled) setQualityByCustomer(summariesByCustomer(rows)); })
      .catch(() => { if (!cancelled) setQualityByCustomer(new Map()); });
    return () => { cancelled = true; };
  }, [customers]);

  // Debounced search
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearchInput = useDebouncedValue(searchInput);
  useEffect(() => {
    setSearch(debouncedSearchInput);
    setOffset(0);
    if (debouncedSearchInput) {
      trackEvent('search', 'customer_search', { query: debouncedSearchInput });
    }
  }, [debouncedSearchInput]);

  // Salesperson list for filter dropdown — fetched from the dedicated endpoint
  const [salespersonOptions, setSalespersonOptions] = useState<{ no: string; name: string }[]>([]);
  useEffect(() => {
    (async () => {
      try {
        const salespersons = await portalService.listSalespersons();
        setSalespersonOptions(
          salespersons.map((sp) => ({ no: sp.salesperson_no, name: sp.name }))
        );
      } catch {
        // Salesperson filter fails silently — not critical
      }
    })();
  }, []);

  // Regional responsible (core salesperson) options for the filter dropdown
  const [regionalOptions, setRegionalOptions] = useState<RegionalResponsibleOption[]>([]);
  useEffect(() => {
    (async () => {
      try {
        setRegionalOptions(await customerService.listRegionalResponsibles());
      } catch {
        // Regional responsible filter fails silently — not critical
      }
    })();
  }, []);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: CustomerListParams = {
        limit: LIMIT,
        offset,
        sort_by: sortBy,
        sort_dir: sortDir,
      };
      if (search) params.search = search;
      if (stateFilter) params.state = stateFilter;
      if (salespersonFilter) params.salesperson_no = salespersonFilter;
      if (regionalFilter) params.regional_responsible = regionalFilter;
      if (myCustomers) params.my_customers = true;
      if (!showInactive) params.status = 'A';

      const res = await customerService.listCustomers(params);
      setCustomers(res.customers);
      setTotal(res.total);
    } catch (err) {
      setError('Failed to load customers');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, stateFilter, salespersonFilter, regionalFilter, myCustomers, showInactive, offset, sortBy, sortDir]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  function handleSort(key: SortKey) {
    trackEvent('filter', 'customer_sort', { sort_by: key });
    if (sortBy === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir(DEFAULT_SORT_DIR[key]);
    }
    setOffset(0);
  }

  function renderSortableHeader(label: string, key: SortKey, align?: 'right') {
    return (
      <th
        className={styles.sortableHeader}
        style={align === 'right' ? { textAlign: 'right' } : undefined}
        onClick={() => handleSort(key)}
        role="columnheader"
        aria-sort={sortBy === key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        <span className={styles.headerContent}>
          {label}
          <SortIcon active={sortBy === key} direction={sortDir} />
        </span>
      </th>
    );
  }

  return (
    <div className={styles.page}>
      {/* What the quality desk is waiting on this person for (kanban 2a28d4c0). */}
      <QualityAsksCard title="Quality asked you" surface="customers" showQuestionsLink />
      <div className={styles.pageHeader}>
        <div className={styles.titleRow}>
          <h1 className={styles.pageTitle}>Customers</h1>
          <button
            type="button"
            className={styles.addCustomerBtn}
            onClick={() => { trackEvent('action', 'add_customer_click'); setShowAddModal(true); }}
            data-action="add_customer_click"
            data-outcome="open"
          >
            + Add Customer
          </button>
          <button
            type="button"
            className={styles.reportComplaintBtn}
            onClick={() => {
              trackEvent('action', 'complaint_report_open', { surface: 'customers', outcome: 'panel_open' });
              setShowComplaintModal(true);
            }}
            data-outcome="open"
          >
            ⚑ Report a quality complaint
          </button>
        </div>
        <div className={styles.searchBar}>
          <input
            className={styles.searchInput}
            type="text"
            placeholder="Search by name or customer number..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <select
            className={styles.filterSelect}
            data-action="customer_state_filter"
            data-record-value="state"
            value={stateFilter}
            onChange={(e) => {
              trackEvent('filter', 'customer_state_filter', { state: e.target.value });
              setStateFilter(e.target.value);
              setOffset(0);
            }}
          >
            <option value="">All States</option>
            {['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'].map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <select
            className={styles.filterSelect}
            data-action="customer_salesperson_filter"
            data-record-value="salesperson"
            value={salespersonFilter}
            onChange={(e) => {
              trackEvent('filter', 'customer_salesperson_filter', { salesperson: e.target.value });
              setSalespersonFilter(e.target.value);
              setOffset(0);
            }}
          >
            <option value="">All Salespersons</option>
            {salespersonOptions.map((sp) => (
              <option key={sp.no} value={sp.no}>{sp.name}</option>
            ))}
          </select>
          <select
            className={styles.filterSelect}
            data-action="customer_regional_responsible_filter"
            data-record-value="regional_responsible"
            value={regionalFilter}
            onChange={(e) => {
              trackEvent('filter', 'customer_regional_responsible_filter', { regional_responsible: e.target.value });
              setRegionalFilter(e.target.value);
              setOffset(0);
            }}
          >
            <option value="">All Regional Responsibles</option>
            {regionalOptions.map((rr) => (
              <option key={rr.id} value={rr.id}>{rr.name}</option>
            ))}
          </select>
          {hasSalesperson && (
            <label className={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={myCustomers}
                onChange={(e) => {
                  setMyCustomers(e.target.checked);
                  setOffset(0);
                }}
                className={styles.toggleCheckbox}
              />
              My customers
            </label>
          )}
          <label className={styles.toggleLabel}>
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => {
                setShowInactive(e.target.checked);
                setOffset(0);
              }}
              className={styles.toggleCheckbox}
            />
            Show inactive
          </label>
        </div>
      </div>

      <div className={styles.tableCard}>
        {loading ? (
          <div className={styles.loading}>Loading customers...</div>
        ) : error ? (
          <div className={styles.error}>{error}</div>
        ) : customers.length === 0 ? (
          <div className={styles.emptyState}>No customers found</div>
        ) : (
          <>
            <div className={styles.tableWrapper}>
              <table className={styles.customerTable}>
                <thead>
                  <tr>
                    {renderSortableHeader('Customer', 'customer_name')}
                    {renderSortableHeader('City', 'city')}
                    {renderSortableHeader('State', 'state')}
                    {renderSortableHeader('Salesperson', 'salesperson_name')}
                    {renderSortableHeader('Regional Resp.', 'regional_responsible_name')}
                    {showInactive && <th>Status</th>}
                    {renderSortableHeader('YTD Sales', 'ytd_sales', 'right')}
                    {renderSortableHeader('vs LYTD', 'lytd_sales', 'right')}
                    {renderSortableHeader('All-Time', 'all_time_sales', 'right')}
                    {renderSortableHeader('Type', 'ly_total_sales')}
                  </tr>
                </thead>
                <tbody>
                  {customers.map((c) => (
                    <tr
                      key={c.id}
                      className={styles.customerRow}
                      onClick={() => { trackEvent('click', 'customer_open', { customer_no: c.sage_customer_no }); navigate(`/portal/customers/${c.slug || c.sage_customer_no}`); }}
                      data-action="customer_open"
                      data-target-customer-no={c.sage_customer_no}
                      data-outcome="navigate"
                    >
                      <td>
                        <div className={styles.customerName}>{c.customer_name || '(unnamed)'}</div>
                        <div className={styles.customerNo}>{c.sage_customer_no}</div>
                        <ComplaintPill summary={qualityByCustomer.get((c.sage_customer_no || '').toUpperCase())} />
                      </td>
                      <td>{c.city || '\u2014'}</td>
                      <td>{c.state || '\u2014'}</td>
                      <td>{c.salesperson_name || c.salesperson_no || '\u2014'}</td>
                      <td>{c.regional_responsible_name || '\u2014'}</td>
                      {showInactive && (
                        <td>
                          <span
                            className={styles.badge}
                            style={{
                              backgroundColor: STATUS_COLORS[c.customer_status || '']?.bg || '#f3f4f6',
                              color: STATUS_COLORS[c.customer_status || '']?.fg || '#6b7280',
                            }}
                          >
                            {CUSTOMER_STATUS_LABELS[c.customer_status || ''] || c.customer_status || '\u2014'}
                          </span>
                        </td>
                      )}
                      <td style={{ textAlign: 'right', fontWeight: 500 }}>
                        {formatMoney(c.ytd_sales)}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {c.ytd_vs_lytd_pct != null ? (
                          <span className={c.ytd_vs_lytd_pct >= 0 ? styles.salesUp : styles.salesDown}>
                            {c.ytd_vs_lytd_pct >= 0 ? '+' : ''}{c.ytd_vs_lytd_pct.toFixed(1)}%
                          </span>
                        ) : '\u2014'}
                      </td>
                      <td style={{ textAlign: 'right', color: '#6b7280' }}>
                        {formatMoney(c.all_time_sales)}
                      </td>
                      <td>{c.industry_type || c.customer_type || '\u2014'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={styles.pagination}>
              <span>
                Showing {offset + 1}&ndash;{Math.min(offset + LIMIT, total)} of {total}
              </span>
              <div className={styles.paginationButtons}>
                <button
                  className={styles.paginationBtn}
                  onClick={() => setOffset(Math.max(0, offset - LIMIT))}
                  disabled={offset === 0}
                >
                  Previous
                </button>
                <button
                  className={styles.paginationBtn}
                  onClick={() => setOffset(offset + LIMIT)}
                  disabled={offset + LIMIT >= total}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {showComplaintModal && (
        <ReportComplaintModal
          customers={customers
            .filter((c) => Boolean(c.sage_customer_no))
            .map((c) => ({ customer_no: c.sage_customer_no, name: c.customer_name }))}
          surface="customers"
          onClose={() => setShowComplaintModal(false)}
        />
      )}

      {showAddModal && (
        <AddCustomerModal
          onCreated={() => {
            setShowAddModal(false);
            fetchCustomers();
          }}
          onCancel={() => setShowAddModal(false)}
        />
      )}
    </div>
  );
}
